import Grid from '@material-ui/core/Grid';
import { MADE_ENCOUNTERS_FILTERNAME } from 'bulk-operations/constants';
import { endOfDay, isValid, parseISO, startOfDay } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import DateField from 'styleguide/form/Date';
import { Field } from '../types';

export interface Props extends Field {
  endLabel?: string;
  endMaxDate?: string;
  endMinDate?: string;
  endName?: string;
  endRequired?: boolean;
  startMinDate?: string;
  onChange: (data: fhir.Period) => void;
  startLabel?: string;
  startMaxDate?: string;
  startName?: string;
  startRequired?: boolean;
  value: fhir.Period | undefined;
  type?: string;
  useUtcCalendarDate?: boolean;
}

const toUtcCalendarDay = (date: Date, isEndOfDay = false) =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      isEndOfDay ? 23 : 0,
      isEndOfDay ? 59 : 0,
      isEndOfDay ? 59 : 0,
      isEndOfDay ? 999 : 0
    )
  ).toISOString();

const Period: React.FC<Props> = ({
  value: data,
  endLabel = 'End',
  endName = 'end',
  onChange,
  onValidate,
  required,
  startRequired,
  endRequired,
  startLabel = 'Start',
  startName = 'start',
  startMinDate,
  startMaxDate,
  endMinDate,
  type,
  useUtcCalendarDate,
}) => {
  const [isStartValid, setIsStartValid] = useState<boolean>(false);
  const [isEndValid, setIsEndValid] = useState<boolean>(false);
  const [maxEndDate, setMaxEndDate] = useState<string | undefined>();
  const handleStartChange = useCallback(
    (value: fhir.dateTime) => {
      const date = parseISO(value);
      if (isValid(date)) {
        let endMaxDate;
        if (type === MADE_ENCOUNTERS_FILTERNAME) {
          endMaxDate = new Date(
            new Date(value).setDate(new Date(value).getDate() + 14)
          ).toISOString();
          setMaxEndDate(endMaxDate);
        }
        const start = useUtcCalendarDate
          ? toUtcCalendarDay(date)
          : startOfDay(date).toISOString();
        onChange({
          ...(data || {}),
          start,
          end: type === MADE_ENCOUNTERS_FILTERNAME ? endMaxDate : data?.end,
        });
      }
    },
    [data, onChange, type, useUtcCalendarDate]
  );

  useEffect(() => {
    if (type === MADE_ENCOUNTERS_FILTERNAME && data?.start) {
      const endMaxDate = new Date(
        new Date(data?.start).setDate(new Date(data?.start).getDate() + 14)
      ).toISOString();
      setMaxEndDate(endMaxDate);
    }
  }, [data, type]);

  const handleEndChange = useCallback(
    (value: fhir.dateTime) => {
      const date = parseISO(value);
      if (isValid(date)) {
        const end = useUtcCalendarDate
          ? toUtcCalendarDay(date, true)
          : endOfDay(date).toISOString();
        onChange({
          ...(data || {}),
          end,
        });
      }
    },
    [data, onChange, useUtcCalendarDate]
  );

  useEffect(() => {
    onValidate(isStartValid && isEndValid);
  }, [isStartValid, isEndValid, onValidate]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <DateField
          required={startRequired || required}
          label={startLabel}
          name={startName}
          data={data?.start}
          onChange={handleStartChange}
          onValidate={setIsStartValid}
          minDate={startMinDate}
          maxDate={startMaxDate || data?.end}
          useUtcCalendarDate={useUtcCalendarDate}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DateField
          required={endRequired || required}
          label={endLabel}
          name={endName}
          data={data?.end}
          onChange={handleEndChange}
          onValidate={setIsEndValid}
          minDate={endMinDate || data?.start || startMinDate}
          maxDate={maxEndDate}
          useUtcCalendarDate={useUtcCalendarDate}
        />
      </Grid>
    </Grid>
  );
};

export default React.memo(Period);
